import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { Authenticators } from '@customerio/hydra-authentication';
import { session } from '@/services/session';
import { useSignupPublicFlags } from '@/services/signup/public-flags';
import { hasSignupPublicFlag, SignupPublicFeatureFlag } from '@/services/signup/public-flags-store';
import { aliasSignupUser } from '@/services/signup/alias';
import { useRecaptchaBadge } from '@/services/signup/recaptcha';
import {
  buildRegisterAccountBody,
  getPromoCode,
  registerAccount,
  startSignup,
  type PromoCode,
  type SignupPrepopulateData,
} from '@/services/signup/api';
import { createDefaultSignupFormValues, type NavigateToSignupStep, type SignupFormValues } from './types';
import type { SignupStep } from './utils/steps';
import {
  getNextStep,
  recommendedPlan as recommendPlan,
  resolveSignupType,
  resolveSignupTypeForTracking,
  shouldShowPlanChoice as computeShouldShowPlanChoice,
  type ResolvedSignupType,
} from './utils/flow-sequence';
import { resolveGuardedStep } from './utils/step-guards';
import {
  SignupAnalyticsEvent,
  signupTypeProps,
  trackSignupEvent,
  type SignupAnalyticsEventName,
} from './utils/analytics';
import { captureLandingUrlParams } from './utils/landing-params';
import { resolveSignupErrorMessage } from './utils/signup-error-message';
import { trackSignupProgress } from './utils/track-signup-progress';
import { clearStoredSignupDraft, saveWelcomeDialogAnswers } from './utils/welcome-dialog';

const ALLOWED_SIGNUP_TYPES = new Set(['journeys', 'builder', 'startup']);

export type SelectedPlan = 'essentials' | 'builders' | null;

export type SignupFlowValue = {
  // Form state. Steps patch as the visitor answers; nothing is persisted —
  // no account or user exists until the final register call.
  formValues: SignupFormValues;
  patchFormValues: (patch: Partial<SignupFormValues>) => void;

  // Onboarding selections (Ember kept these beside formValues, not in it).
  messagingUseCases: string[];
  setMessagingUseCases: (value: string[]) => void;
  messageChannels: string[];
  setMessageChannels: (value: string[]) => void;
  onboardingRole: string | null;
  setOnboardingRole: (value: string | null) => void;
  selectedPlan: SelectedPlan;
  setSelectedPlan: (value: SelectedPlan) => void;

  // Flow classification (derived).
  /** Raw `?signupType=` — unvalidated; prefer `effectiveSignupType`. */
  signupType: string | undefined;
  /**
   * The validated signup type this funnel run operates as (Ember's
   * `model.signupType.name`): unknown values coerce to `journeys`, and
   * `startup` requires the `signup_startup_form` public flag. Step analytics
   * pass this as `Signup Type`.
   */
  effectiveSignupType: string;
  isStartup: boolean;
  isBuilder: boolean;
  isGoogleSignup: boolean;
  isSignupDarkModeEnabled: boolean;
  shouldShowPlanChoice: boolean;
  recommendedPlan: 'builders' | 'essentials';
  resolvedSignupType: ResolvedSignupType;
  /** For step analytics: `undefined` while a shown plan choice is undecided. */
  resolvedSignupTypeForTracking: ResolvedSignupType | undefined;

  // Promo.
  promoCode: string | undefined;
  /** Set once the URL promo validates; stays `null` for unknown/redeemed codes. */
  validatedPromo: PromoCode | null;

  // Sequence.
  currentStep: SignupStep;
  /**
   * Advance the funnel: fires the step's server-side identify, then navigates
   * to the next step — or, at the end of the sequence, creates the account
   * (register → session flip → welcome-dialog handoff → analytics alias).
   */
  goToNextStep: (options?: { skipTracking?: boolean }) => Promise<void>;
  /** The end-of-sequence register is in flight. */
  isCompletingSignup: boolean;

  /**
   * The entry step's submit plumbing: reCAPTCHA-guarded `account_signup/start`,
   * prepopulate handling, the `NameAndEmailSubmitted` event, and the first
   * `goToNextStep`. Errors propagate — the entry step owns their display.
   */
  beginSignup: (email: string) => Promise<SignupPrepopulateData>;

  trackSignupEvent: (event: SignupAnalyticsEventName, properties?: Record<string, unknown>) => void;
};

const SignupFlowContext = createContext<SignupFlowValue | null>(null);

export function useSignupFlow(): SignupFlowValue {
  const value = use(SignupFlowContext);
  if (value == null) {
    throw new Error('useSignupFlow must be used within a SignupFlowProvider');
  }
  return value;
}

/**
 * `useState` with a write-through ref. Step submits set state and call the
 * flow engine in the same tick (`setMessageChannels(x); goToNextStep()`), so
 * the engine must read through refs — a plain closure would still see the
 * previous render's values and register the account with stale answers.
 */
function useStateWithRef<T>(initial: T): [T, (next: T) => void, RefObject<T>] {
  const ref = useRef(initial);
  const [value, setValue] = useState(initial);
  const set = useCallback((next: T) => {
    ref.current = next;
    setValue(next);
  }, []);
  return [value, set, ref];
}

/** Props/flags the engine reads through a ref (they never change mid-tick from a step action). */
type FlowFlags = {
  isStartup: boolean;
  isBuilder: boolean;
  effectiveSignupType: string;
  hasPromoCode: boolean;
  promoCode: string | undefined;
};

/**
 * Classify the funnel run from its URL params + public flags (Ember's route
 * model): unknown signup types coerce to `journeys`, and a startup arrival
 * without the `signup_startup_form` flag downgrades to `journeys`.
 */
function classifySignupFlow(
  signupType: string | undefined,
  promoCode: string | undefined,
  flags: Parameters<typeof hasSignupPublicFlag>[0],
): FlowFlags & { isSignupDarkModeEnabled: boolean } {
  const validatedSignupType = signupType != null && ALLOWED_SIGNUP_TYPES.has(signupType) ? signupType : 'journeys';
  const isStartup =
    validatedSignupType === 'startup' && hasSignupPublicFlag(flags, SignupPublicFeatureFlag.SignupStartupForm);
  return {
    isStartup,
    isBuilder: validatedSignupType === 'builder',
    effectiveSignupType: validatedSignupType === 'startup' && !isStartup ? 'journeys' : validatedSignupType,
    hasPromoCode: promoCode != null && promoCode !== '',
    promoCode,
    isSignupDarkModeEnabled: hasSignupPublicFlag(flags, SignupPublicFeatureFlag.SignupDarkMode),
  };
}

/**
 * Apply the funnel-integrity guards: flow state lives in memory only, so deep
 * links and refreshes past the entry step have nothing to work with — the
 * provider renders nothing and bounces to the guard's target step.
 */
function useStepGuard(
  step: SignupStep,
  context: Parameters<typeof resolveGuardedStep>[1],
  onNavigateStep: NavigateToSignupStep,
): boolean {
  const guardedStep = resolveGuardedStep(step, context);
  const isGuarded = guardedStep !== step;
  useEffect(() => {
    if (isGuarded) {
      // Replace, never push: the bounce must consume the rejected URL. Pushing
      // would leave it behind for Back to return to, where the guard bounces
      // again — trapping the visitor on the funnel entry.
      onNavigateStep(guardedStep, { replace: true });
    }
  }, [isGuarded, guardedStep, onNavigateStep]);
  return isGuarded;
}

/** The funnel's writable state: form values plus the onboarding selections. */
function useSignupFlowState(initialFormValues: Partial<SignupFormValues> | undefined, isBuilder: boolean) {
  const [formValues, setFormValuesInternal, formValuesRef] = useStateWithRef<SignupFormValues>(
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial state only; later prop changes are meaningless
    useMemo(() => ({ ...createDefaultSignupFormValues(), ...initialFormValues }), []),
  );
  const [messagingUseCases, setMessagingUseCases, messagingUseCasesRef] = useStateWithRef<string[]>([]);
  const [messageChannels, setMessageChannels, messageChannelsRef] = useStateWithRef<string[]>([]);
  const [onboardingRole, setOnboardingRole, onboardingRoleRef] = useStateWithRef<string | null>(null);
  // `?signupType=builder` arrivals start on the Builder plan (Ember's route
  // preselect). `isBuilder` comes from the URL alone, so it's known at first
  // render — this is an initial value, not something to sync in an effect.
  const [selectedPlan, setSelectedPlan, selectedPlanRef] = useStateWithRef<SelectedPlan>(isBuilder ? 'builders' : null);

  const patchFormValues = useCallback(
    (patch: Partial<SignupFormValues>) => {
      setFormValuesInternal({ ...formValuesRef.current, ...patch });
    },
    [setFormValuesInternal, formValuesRef],
  );

  return {
    formValues,
    formValuesRef,
    patchFormValues,
    messagingUseCases,
    setMessagingUseCases,
    messagingUseCasesRef,
    messageChannels,
    setMessageChannels,
    messageChannelsRef,
    onboardingRole,
    setOnboardingRole,
    onboardingRoleRef,
    selectedPlan,
    setSelectedPlan,
    selectedPlanRef,
  };
}

type SignupFlowState = ReturnType<typeof useSignupFlowState>;

/**
 * One-time funnel-entry side effects: landing-param capture (before step
 * navigation rewrites the URL — Ember's route `beforeModel`) and URL-promo
 * validation for the coupon banner (fire-and-forget; signup is never gated on
 * it).
 */
function useEntryEffects(promoCode: string | undefined): PromoCode | null {
  const [validatedPromo, setValidatedPromo] = useState<PromoCode | null>(null);

  useEffect(() => {
    captureLandingUrlParams(Object.fromEntries(new URLSearchParams(window.location.search)));
    clearStoredSignupDraft();
  }, []);

  useEffect(() => {
    if (promoCode == null || promoCode === '') {
      return;
    }
    let cancelled = false;
    void getPromoCode(promoCode).then((promo) => {
      if (!cancelled) {
        setValidatedPromo(promo);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [promoCode]);

  return validatedPromo;
}

/**
 * End of sequence: create the account. Order is Ember's: register → flip the
 * local session (the server already set the cookie; `AccountCreation` is the
 * no-op authenticator for exactly this) → persist the welcome-dialog answers
 * for the authenticated app → alias the anonymous analytics id to the new
 * user. The auth flip triggers the page's `isAuthenticated` redirect into
 * the app.
 */
function useCompleteSignup(state: SignupFlowState, flowFlagsRef: RefObject<FlowFlags>) {
  const snackbars = useSnackbars();
  const [isCompletingSignup, setIsCompletingSignup] = useState(false);
  // Synchronous re-entrancy guard: `isCompletingSignup` state flushes a render
  // late, so a rapid double submit at the last step would otherwise fire two
  // register calls before the first one resolves.
  const isCompletingRef = useRef(false);
  const {
    formValuesRef,
    patchFormValues,
    messagingUseCasesRef,
    messageChannelsRef,
    onboardingRoleRef,
    selectedPlanRef,
  } = state;

  const completeSignup = useCallback(async () => {
    if (isCompletingRef.current) {
      return;
    }
    isCompletingRef.current = true;

    const { isStartup, isBuilder, hasPromoCode, promoCode } = flowFlagsRef.current;
    const resolved = resolveSignupType({
      isStartup,
      isBuilder,
      showPlanChoice: computeShouldShowPlanChoice({
        hasPromoCode,
        messagingUseCases: messagingUseCasesRef.current,
      }),
      selectedPlan: selectedPlanRef.current,
    });
    patchFormValues({ signupType: resolved });

    // Ember parity: the *raw* trimmed URL code goes to the register call — the
    // server is the promo authority; `validatedPromo` only drives the banner.
    // An invalid code fails the register with promo-specific copy (below)
    // rather than being silently dropped, which could lose a legitimate promo
    // whose pre-validation merely failed transiently.
    const promo = promoCode?.trim();
    const registerPromoCode = promo != null && promo !== '' ? promo : undefined;

    setIsCompletingSignup(true);
    try {
      const body = buildRegisterAccountBody({
        values: formValuesRef.current,
        messagingUseCases: messagingUseCasesRef.current,
        optInChannels: [],
        emailVolume: null,
        role: onboardingRoleRef.current,
        messageChannels: messageChannelsRef.current,
        promoCode: registerPromoCode,
      });
      const { user } = await registerAccount(body);
      await session.authenticate(Authenticators.AccountCreation, undefined);
      saveWelcomeDialogAnswers({
        messagingUseCases: messagingUseCasesRef.current,
        v2MessageChannels: messageChannelsRef.current,
        v2Role: onboardingRoleRef.current,
        selectedPlan: selectedPlanRef.current,
      });
      await aliasSignupUser(user.id);
    } catch (error) {
      snackbars.create({
        variant: 'error',
        id: 'signup.error',
        message: resolveSignupErrorMessage(error, registerPromoCode),
      });
    } finally {
      isCompletingRef.current = false;
      setIsCompletingSignup(false);
    }
  }, [
    flowFlagsRef,
    formValuesRef,
    messagingUseCasesRef,
    messageChannelsRef,
    onboardingRoleRef,
    selectedPlanRef,
    patchFormValues,
    snackbars,
  ]);

  return { completeSignup, isCompletingSignup };
}

/** The sequencing engine: `goToNextStep` + the entry step's `beginSignup` plumbing. */
function useSignupEngine(
  state: SignupFlowState,
  flowFlagsRef: RefObject<FlowFlags>,
  stepRef: RefObject<SignupStep>,
  onNavigateStep: NavigateToSignupStep,
) {
  const { formValuesRef, patchFormValues, messagingUseCasesRef } = state;
  const { completeSignup, isCompletingSignup } = useCompleteSignup(state, flowFlagsRef);

  const goToNextStep = useCallback(
    async ({ skipTracking = false }: { skipTracking?: boolean } = {}) => {
      if (!skipTracking) {
        trackSignupProgress(stepRef.current, formValuesRef.current, flowFlagsRef.current);
      }

      const { isStartup, isBuilder, hasPromoCode } = flowFlagsRef.current;
      const next = getNextStep(stepRef.current, {
        isStartup,
        isGoogleSignup: formValuesRef.current.isGoogleSignup === true,
        showPlanChoice: computeShouldShowPlanChoice({
          hasPromoCode,
          messagingUseCases: messagingUseCasesRef.current,
        }),
        isBuilder,
      });

      // Off-sequence steps are the guards' job; unlike Ember (which fell
      // through to account creation here) advancing from one is a no-op.
      if (next == null) {
        return;
      }

      if (next === 'complete') {
        await completeSignup();
        return;
      }

      onNavigateStep(next);
    },
    [completeSignup, onNavigateStep, stepRef, flowFlagsRef, formValuesRef, messagingUseCasesRef],
  );

  const runBeginSignup = useCallback(
    async (email: string): Promise<SignupPrepopulateData> => {
      patchFormValues({ email });
      const { effectiveSignupType } = flowFlagsRef.current;

      const prepopulate = await startSignup({
        email,
        isGoogleSignup: formValuesRef.current.isGoogleSignup,
        signupType: effectiveSignupType,
      });

      const values = formValuesRef.current;
      patchFormValues({
        ...(values.companyName === '' ? { companyName: prepopulate.companyName } : {}),
        ...(values.role === '' ? { role: prepopulate.role } : {}),
      });

      trackSignupEvent(
        SignupAnalyticsEvent.NameAndEmailSubmitted,
        signupTypeProps({ signupType: effectiveSignupType, isGoogleSignup: formValuesRef.current.isGoogleSignup }),
      );

      await goToNextStep();
      return prepopulate;
    },
    [patchFormValues, goToNextStep, flowFlagsRef, formValuesRef],
  );

  // Single-flight: `beginSignup` has a long async window (the start call), and
  // each completion advances from the *current* step — so a second submit
  // landing after the first advanced off the entry step would advance again
  // and skip the confirmation (email-verification) step. Concurrent submits
  // share the in-flight run instead.
  const inflightBeginRef = useRef<Promise<SignupPrepopulateData> | null>(null);
  const beginSignup = useCallback(
    (email: string): Promise<SignupPrepopulateData> => {
      inflightBeginRef.current ??= runBeginSignup(email).finally(() => {
        inflightBeginRef.current = null;
      });
      return inflightBeginRef.current;
    },
    [runBeginSignup],
  );

  return { goToNextStep, beginSignup, isCompletingSignup };
}

type SignupFlowProviderProps = {
  children: ReactNode;
  step: SignupStep;
  onNavigateStep: NavigateToSignupStep;
  signupType: string | undefined;
  promoCode: string | undefined;
  /** See `SignupPageProps.initialFormValues`. */
  initialFormValues?: Partial<SignupFormValues>;
};

/**
 * The signup funnel's state + sequencing engine, ported from the Ember signup
 * controller (FPS-2852). Steps (PRs 3–7) consume it through `useSignupFlow`:
 * patch answers, fire their step event, call an API-module function if they
 * have one, then `goToNextStep`.
 *
 * The engine deliberately owns everything that is invisible in the UI and so
 * easiest to drop in a port: the per-step server-side identify, the final
 * register payload (attribution cookies included), the session flip, the
 * welcome-dialog handoff, and the anonymous-id alias.
 */
export function SignupFlowProvider({
  children,
  step,
  onNavigateStep,
  signupType,
  promoCode,
  initialFormValues,
}: SignupFlowProviderProps) {
  const flags = useSignupPublicFlags();
  useRecaptchaBadge();

  // Classify first: the state hook seeds `selectedPlan` from `isBuilder`.
  const classification = classifySignupFlow(signupType, promoCode, flags);
  const { isStartup, isBuilder, effectiveSignupType, hasPromoCode, isSignupDarkModeEnabled } = classification;

  const state = useSignupFlowState(initialFormValues, isBuilder);

  const flowFlagsRef = useRef<FlowFlags>(classification);
  flowFlagsRef.current = classification;
  const stepRef = useRef(step);
  stepRef.current = step;

  const validatedPromo = useEntryEffects(promoCode);
  const { goToNextStep, beginSignup, isCompletingSignup } = useSignupEngine(
    state,
    flowFlagsRef,
    stepRef,
    onNavigateStep,
  );

  const showPlanChoice = computeShouldShowPlanChoice({ hasPromoCode, messagingUseCases: state.messagingUseCases });

  // The guard needs the full sequence context: a step renders only while the
  // active sequence contains it, so history back/forward can't resurface a
  // step the visitor's answers have since removed.
  const isGuarded = useStepGuard(
    step,
    {
      hasEmail: state.formValues.email.trim() !== '',
      isStartup,
      isGoogleSignup: state.formValues.isGoogleSignup === true,
      showPlanChoice,
      isBuilder,
    },
    onNavigateStep,
  );
  const value = useMemo<SignupFlowValue>(
    () => ({
      formValues: state.formValues,
      patchFormValues: state.patchFormValues,
      messagingUseCases: state.messagingUseCases,
      setMessagingUseCases: state.setMessagingUseCases,
      messageChannels: state.messageChannels,
      setMessageChannels: state.setMessageChannels,
      onboardingRole: state.onboardingRole,
      setOnboardingRole: state.setOnboardingRole,
      selectedPlan: state.selectedPlan,
      setSelectedPlan: state.setSelectedPlan,
      signupType,
      effectiveSignupType,
      isStartup,
      isBuilder,
      isGoogleSignup: state.formValues.isGoogleSignup === true,
      isSignupDarkModeEnabled,
      shouldShowPlanChoice: showPlanChoice,
      recommendedPlan: recommendPlan(showPlanChoice),
      resolvedSignupType: resolveSignupType({ isStartup, isBuilder, showPlanChoice, selectedPlan: state.selectedPlan }),
      resolvedSignupTypeForTracking: resolveSignupTypeForTracking({
        isStartup,
        isBuilder,
        showPlanChoice,
        selectedPlan: state.selectedPlan,
      }),
      promoCode,
      validatedPromo,
      currentStep: step,
      goToNextStep,
      isCompletingSignup,
      beginSignup,
      trackSignupEvent,
    }),
    [
      state.formValues,
      state.patchFormValues,
      state.messagingUseCases,
      state.setMessagingUseCases,
      state.messageChannels,
      state.setMessageChannels,
      state.onboardingRole,
      state.setOnboardingRole,
      state.selectedPlan,
      state.setSelectedPlan,
      signupType,
      effectiveSignupType,
      isStartup,
      isBuilder,
      isSignupDarkModeEnabled,
      showPlanChoice,
      promoCode,
      validatedPromo,
      step,
      goToNextStep,
      isCompletingSignup,
      beginSignup,
    ],
  );

  if (isGuarded) {
    return null;
  }

  return <SignupFlowContext value={value}>{children}</SignupFlowContext>;
}
